import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, UserPlus, Gift, Share2, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { RankShare } from "@/components/RankShare";
import { Page, PageHead, Pill } from "@/components/hub/Common";
import { shareUrl } from "@/components/ShareToggle";
import { StyleTable } from "@/components/publish/StyleTable";

const Stat = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className="cx-card rounded-2xl p-5 relative overflow-hidden" data-testid={testid}>
    <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-3xl opacity-30" style={{ background: color }} />
    <Icon className="w-5 h-5" style={{ color }} />
    <div className="font-display text-4xl font-bold text-white mt-3">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1 font-mono">{sub}</div>}
  </div>
);

const FEAT = { none: "slate", pending: "amber", approved: "green", rejected: "rose" };

const Row = ({ r, max, t }) => (
  <div className="grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,1fr)_200px_90px_90px_auto] gap-3 md:gap-4 items-center px-4 py-3 border-t border-white/[0.05]" data-testid={`stats-row-${r.id}`}>
    <div className="min-w-0">
      <div className="text-sm text-white truncate">{r.title}</div>
      <div className="flex items-center gap-2 mt-1">
        <Pill tone={r.shared ? "cyan" : "slate"}>{r.shared ? t("stt.public") : t("stt.private")}</Pill>
        {r.featured_status !== "none" && <Pill tone={FEAT[r.featured_status]}>{t(`stt.feat_${r.featured_status}`)}</Pill>}
      </div>
    </div>
    <div className="hidden md:block h-2 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#2f7bff] to-[#3ad7ff]" style={{ width: `${max ? Math.max(3, (r.views / max) * 100) : 0}%` }} />
    </div>
    <div className="text-sm font-mono text-white flex items-center gap-1.5" data-testid={`stats-views-${r.id}`}><Eye className="w-3.5 h-3.5 text-[#3ad7ff]" />{r.views}</div>
    <div className="text-sm font-mono text-white flex items-center gap-1.5" data-testid={`stats-signups-${r.id}`}><UserPlus className="w-3.5 h-3.5 text-[#c79bff]" />{r.signups}</div>
    <div className="flex items-center gap-2 justify-end col-span-2 md:col-span-1">
      {r.shared && (
        <button onClick={async () => { await navigator.clipboard.writeText(shareUrl(r.share_id)).catch(() => {}); toast.success(t("sh.copied")); }}
          className="text-[#3ad7ff] hover:text-white" title={t("sh.copy")} data-testid={`stats-copy-${r.id}`}><Link2 className="w-4 h-4" /></button>
      )}
      {r.shared && <Link to={`/c/${r.share_id}`} target="_blank" className="text-xs text-slate-400 hover:text-white" data-testid={`stats-open-${r.id}`}>{t("stt.open")}</Link>}
    </div>
  </div>
);

export default function Stats() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/stats/shares").then((r) => setD(r.data)).catch(() => setD({ totals: {}, clips: [] })), []);
  useEffect(() => { load(); }, [load]);
  const tt = d?.totals || {};
  const max = Math.max(0, ...(d?.clips || []).map((r) => r.views));
  return (
    <Page>
      <PageHead label={t("stt.label")} t1={t("stt.title1")} t2={t("stt.title2")} sub={t("stt.sub")} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Eye} label={t("stt.views")} value={tt.views ?? "—"} color="#3ad7ff" testid="stats-total-views" />
        <Stat icon={UserPlus} label={t("stt.signups")} value={tt.signups ?? "—"} sub={tt.pending ? `${tt.pending} ${t("stt.pending")}` : ""} color="#a746ff" testid="stats-total-signups" />
        <Stat icon={Gift} label={t("stt.bonus")} value={tt.bonus_earned ?? "—"} sub={`${tt.month_rewarded ?? 0}/${tt.month_cap ?? 10} ${t("stt.this_month")}`} color="#ffc94d" testid="stats-total-bonus" />
        <Stat icon={Sparkles} label={t("stt.balance")} value={tt.bonus_balance ?? "—"} color="#53fc18" testid="stats-bonus-balance" />
      </div>
      <StyleTable />
      <RankShare />
      <div className="cx-card rounded-2xl p-5 mt-6 flex items-start gap-3 text-sm text-slate-300" data-testid="stats-how-it-works">
        <Gift className="w-5 h-5 text-[#ffc94d] shrink-0 mt-0.5" /><p>{t("stt.how")}</p>
      </div>
      <div className="cx-card rounded-2xl mt-6 overflow-hidden" data-testid="stats-table">
        <div className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2"><Share2 className="w-3.5 h-3.5" />{t("stt.per_clip")}</div>
        {d && d.clips.length === 0 && <p className="px-4 py-10 text-center text-sm text-slate-500 border-t border-white/[0.05]" data-testid="stats-empty">{t("stt.empty")}</p>}
        {d?.clips.map((r) => <Row key={r.id} r={r} max={max} t={t} />)}
      </div>
    </Page>
  );
}
